<template>
  <section id="apiTokens">
    <b-card
      id="apiTokensCard"
      class="blockDrawer large"
      :class="$store.state.drawer.apiTokens.position"
    >
      <template v-slot:header>
        <a
          @click="closeDrawer()"
          class="close clickable"
        >
          <i class="far fa-times"></i>
        </a>
        <h4>API Tokens</h4>
        <p class="text-muted mb-0" style="font-size: 0.85rem;">
          Generate tokens for 3rd-party integrations to access your data.
        </p>
      </template>

      <!-- Create New Token -->
      <div class="mb-4">
        <h5>Create New Token</h5>
        <form @submit.prevent="createToken()">
          <div class="form-group">
            <label for="tokenName">Token Name</label>
            <input
              v-model="newToken.name"
              type="text"
              id="tokenName"
              class="form-control"
              placeholder="e.g. My Integration"
              required
            />
          </div>

          <div class="form-group">
            <label>Permissions</label>
            <b-form-checkbox
              v-model="newToken.scopes"
              value="read"
              disabled
            >
              Read — access farms, datasets, and GeoJSON
            </b-form-checkbox>
            <b-form-checkbox
              v-model="newToken.scopes"
              value="write"
            >
              Write — push datasets and update GeoJSON
            </b-form-checkbox>
          </div>

          <div class="form-group">
            <label class="d-flex justify-content-between align-items-center">
              <span>Restrict to Farms (optional)</span>
              <small v-if="newToken.farmIds.length > 0" class="text-muted">
                {{ newToken.farmIds.length }} selected
              </small>
            </label>
            <div v-if="farms.length > 0">
              <input
                v-model="farmSearch"
                type="search"
                class="form-control form-control-sm mb-2"
                placeholder="Search farms…"
              />
              <div class="farm-picker">
                <b-form-checkbox
                  v-for="farm in filteredFarms"
                  :key="farm.id"
                  v-model="newToken.farmIds"
                  :value="farm.id"
                  class="farm-picker-row"
                >
                  {{ farm.name }}
                </b-form-checkbox>
                <p v-if="filteredFarms.length === 0" class="text-muted small mb-0 px-2 py-1">
                  No farms match “{{ farmSearch }}”.
                </p>
              </div>
              <div class="d-flex justify-content-between align-items-center mt-2">
                <b-btn
                  v-if="filteredFarms.length > 1"
                  size="sm"
                  variant="outline-success"
                  @click="selectAllVisibleFarms"
                >
                  Select all{{ farmSearch ? ' visible' : '' }}
                </b-btn>
                <b-btn
                  v-if="newToken.farmIds.length > 0"
                  size="sm"
                  variant="link"
                  class="text-muted p-0 ml-auto"
                  @click="newToken.farmIds = []"
                >
                  Clear selection
                </b-btn>
              </div>
            </div>
            <small class="text-muted d-block mt-1" v-if="newToken.farmIds.length === 0">
              No restriction — token can access every farm you have permission to.
            </small>
          </div>

          <div class="form-group">
            <label for="tokenExpiry">Expires In</label>
            <select v-model="newToken.expiresInDays" id="tokenExpiry" class="form-control">
              <option :value="null">Never</option>
              <option :value="30">30 days</option>
              <option :value="90">90 days</option>
              <option :value="180">180 days</option>
              <option :value="365">1 year</option>
            </select>
          </div>

          <b-btn
            variant="success"
            @click="createToken()"
            :disabled="!newToken.name || creatingToken"
          >
            <i class="fas fa-plus" v-if="!creatingToken"></i>
            <i class="fas fa-spinner fa-spin" v-if="creatingToken"></i>
            {{ creatingToken ? 'Creating...' : 'Generate Token' }}
          </b-btn>
        </form>
      </div>

      <!-- Newly Created Token (show once) -->
      <div v-if="justCreatedToken" class="alert alert-success mb-4">
        <h6><i class="fas fa-check-circle"></i> Token Created</h6>
        <p class="mb-1" style="font-size: 0.85rem;">
          Copy this token now. It will not be shown again.
        </p>
        <div class="input-group">
          <input
            type="text"
            class="form-control"
            :value="justCreatedToken"
            readonly
            ref="tokenInput"
            style="font-family: monospace; font-size: 0.8rem;"
          />
          <div class="input-group-append">
            <b-btn variant="outline-secondary" @click="copyToken()">
              <i class="far fa-copy"></i> Copy
            </b-btn>
          </div>
        </div>
      </div>

      <hr>

      <!-- Existing Tokens -->
      <h5>Your Tokens</h5>

      <div v-if="loadingTokens" class="text-center py-3">
        <i class="fas fa-spinner fa-spin"></i> Loading tokens...
      </div>

      <div v-else-if="tokens.length === 0" class="text-muted py-3">
        No API tokens yet. Create one above to get started.
      </div>

      <div v-else>
        <div
          v-for="token in tokens"
          :key="token.id"
          class="card mb-2"
          :class="{ 'border-secondary': !token.active }"
        >
          <div class="card-body py-2 px-3">
            <div class="d-flex justify-content-between align-items-start">
              <div>
                <strong>{{ token.name }}</strong>
                <span
                  v-if="!token.active"
                  class="badge badge-secondary ml-1"
                >revoked</span>
                <br>
                <code style="font-size: 0.75rem;">{{ token.tokenPrefix }}</code>
                <br>
                <small class="text-muted">
                  Scopes: {{ token.scopes.join(', ') }}
                  <span v-if="token.farmIds && token.farmIds.length > 0">
                    &middot; {{ token.farmIds.length }} farm{{ token.farmIds.length > 1 ? 's' : '' }}
                  </span>
                </small>
                <br>
                <small class="text-muted">
                  Created
                  <span v-b-tooltip.hover :title="formatDate(token.createdAt)">
                    {{ formatRelative(token.createdAt) }}
                  </span>
                  <span v-if="token.lastUsedAt">
                    &middot; Last used
                    <span v-b-tooltip.hover :title="formatDate(token.lastUsedAt)">
                      {{ formatRelative(token.lastUsedAt) }}
                    </span>
                  </span>
                  <span v-else>
                    &middot; <em>Never used</em>
                  </span>
                  <span v-if="token.expiresAt">
                    &middot; Expires
                    <span v-b-tooltip.hover :title="formatDate(token.expiresAt)">
                      {{ formatRelative(token.expiresAt) }}
                    </span>
                  </span>
                </small>
              </div>
              <b-btn
                v-if="token.active"
                variant="outline-danger"
                size="sm"
                @click="revokeToken(token)"
                :disabled="revokingTokenId === token.id"
              >
                <i class="fas fa-ban"></i> Revoke
              </b-btn>
            </div>
          </div>
        </div>
      </div>

      <hr>

      <!-- API Documentation Quick Reference -->
      <h5>Quick Reference</h5>
      <div style="font-size: 0.85rem;">
        <p class="mb-1"><strong>Base URL:</strong></p>
        <code class="d-block mb-2" style="font-size: 0.75rem;">https://api.every.farm</code>
        <p class="mb-1"><strong>Authentication:</strong></p>
        <code class="d-block mb-2" style="font-size: 0.75rem;">Authorization: Bearer &lt;your-token&gt;</code>
        <p class="mb-1"><strong>Endpoints:</strong></p>
        <table class="table table-sm" style="font-size: 0.8rem;">
          <tbody>
            <tr><td><code>GET</code></td><td>/v1/farms</td><td>List your farms</td></tr>
            <tr><td><code>GET</code></td><td>/v1/farms/:id</td><td>Farm details</td></tr>
            <tr><td><code>GET</code></td><td>/v1/farms/:id/blocks</td><td>Farm blocks</td></tr>
            <tr><td><code>GET</code></td><td>/v1/farms/:id/datasets</td><td>List datasets</td></tr>
            <tr><td><code>GET</code></td><td>/v1/farms/:id/folders</td><td>List folders</td></tr>
            <tr><td><code>GET</code></td><td>/v1/datasets/:id</td><td>Dataset details</td></tr>
            <tr><td><code>GET</code></td><td>/v1/datasets/:id/geojson</td><td>Dataset GeoJSON</td></tr>
            <tr><td><code>POST</code></td><td>/v1/farms/:id/datasets</td><td>Push dataset</td></tr>
            <tr><td><code>PUT</code></td><td>/v1/datasets/:id/geojson</td><td>Update GeoJSON</td></tr>
            <tr><td><code>GET</code></td><td>/v1/usage</td><td>Rate limit status</td></tr>
          </tbody>
        </table>
        <p class="text-muted mb-0">
          Rate limit: 60 requests/min, 10,000 requests/day per token.
        </p>
      </div>

      <hr>
      <div class="float-right">
        <button
          @click="closeDrawer()"
          class="btn btn-secondary"
        >
          Close
        </button>
      </div>
    </b-card>
  </section>
</template>

<script>
import { mapState } from 'vuex'
const fb = require('../../firebaseConfig.js')

export default {
  data () {
    return {
      tokens: [],
      loadingTokens: true,
      creatingToken: false,
      revokingTokenId: null,
      justCreatedToken: null,
      farmSearch: '',
      newToken: {
        name: '',
        scopes: ['read'],
        farmIds: [],
        expiresInDays: null
      }
    }
  },
  computed: {
    ...mapState(['currentUser', 'farms']),
    // Sort the farm picker alphabetically (case-insensitive) so long
    // lists stay scannable, and filter by `farmSearch` substring. The
    // search-input + max-height scroll container turns a 100-farm list
    // into something workable.
    sortedFarms () {
      const list = Array.isArray(this.farms) ? this.farms.slice() : []
      return list.sort((a, b) => {
        const an = (a.name || '').toLowerCase()
        const bn = (b.name || '').toLowerCase()
        if (an < bn) return -1
        if (an > bn) return 1
        return 0
      })
    },
    filteredFarms () {
      const q = (this.farmSearch || '').trim().toLowerCase()
      if (!q) return this.sortedFarms
      return this.sortedFarms.filter(f => (f.name || '').toLowerCase().includes(q))
    }
  },
  created () {
    this.fetchTokens()
  },
  methods: {
    closeDrawer () {
      this.$store.commit('hideDrawer', 'apiTokens')
      setTimeout(() => {
        this.$store.commit('showDrawer', 'userDetails')
      }, 300)
    },

    async getIdToken () {
      return fb.auth.currentUser.getIdToken()
    },

    async fetchTokens () {
      this.loadingTokens = true
      try {
        const idToken = await this.getIdToken()
        const response = await fetch(
          'https://api.every.farm/v1/tokens',
          {
            headers: { Authorization: 'Bearer ' + idToken }
          }
        )
        if (!response.ok) throw new Error('Failed to fetch tokens')
        const data = await response.json()
        this.tokens = data.tokens
      } catch (err) {
        console.error('Failed to load tokens:', err)
        this.$store.commit('setAlert', {
          active: true,
          message: 'Failed to load API tokens.',
          level: 'alert-warning',
          timeout: 3000
        })
      }
      this.loadingTokens = false
    },

    async createToken () {
      if (!this.newToken.name) return
      this.creatingToken = true
      this.justCreatedToken = null

      try {
        const idToken = await this.getIdToken()
        const response = await fetch(
          'https://api.every.farm/v1/tokens',
          {
            method: 'POST',
            headers: {
              Authorization: 'Bearer ' + idToken,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              name: this.newToken.name,
              scopes: this.newToken.scopes,
              farmIds: this.newToken.farmIds,
              expiresInDays: this.newToken.expiresInDays
            })
          }
        )
        if (!response.ok) {
          const err = await response.json()
          throw new Error(err.error || 'Failed to create token')
        }
        const data = await response.json()
        this.justCreatedToken = data.token

        // Reset form
        this.newToken = {
          name: '',
          scopes: ['read'],
          farmIds: [],
          expiresInDays: null
        }

        // Refresh token list
        await this.fetchTokens()

        this.$store.commit('setAlert', {
          active: true,
          message: 'API token created! Copy it now — it won\'t be shown again.',
          level: 'alert-success',
          timeout: 5000
        })
      } catch (err) {
        console.error('Failed to create token:', err)
        this.$store.commit('setAlert', {
          active: true,
          message: err.message || 'Failed to create API token.',
          level: 'alert-warning',
          timeout: 3000
        })
      }
      this.creatingToken = false
    },

    async revokeToken (token) {
      this.revokingTokenId = token.id
      try {
        const idToken = await this.getIdToken()
        const response = await fetch(
          'https://api.every.farm/v1/tokens/' + token.id,
          {
            method: 'DELETE',
            headers: { Authorization: 'Bearer ' + idToken }
          }
        )
        if (!response.ok) throw new Error('Failed to revoke token')

        await this.fetchTokens()

        this.$store.commit('setAlert', {
          active: true,
          message: 'Token revoked.',
          level: 'alert-success',
          timeout: 3000
        })
      } catch (err) {
        console.error('Failed to revoke token:', err)
        this.$store.commit('setAlert', {
          active: true,
          message: 'Failed to revoke token.',
          level: 'alert-warning',
          timeout: 3000
        })
      }
      this.revokingTokenId = null
    },

    copyToken () {
      if (this.$refs.tokenInput) {
        this.$refs.tokenInput.select()
        document.execCommand('copy')
        this.$store.commit('setAlert', {
          active: true,
          message: 'Token copied to clipboard!',
          level: 'alert-success',
          timeout: 2000
        })
      }
    },

    selectAllVisibleFarms () {
      // Union the current selection with everything visible after the
      // search filter. "Select all visible" means the filter narrows
      // what the click affects, not what it overwrites.
      const have = new Set(this.newToken.farmIds)
      this.filteredFarms.forEach(f => have.add(f.id))
      this.newToken.farmIds = Array.from(have)
    },

    formatDate (timestamp) {
      const date = this.toDate(timestamp)
      if (!date) return ''
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    },

    formatRelative (timestamp) {
      const date = this.toDate(timestamp)
      if (!date) return ''
      const diffMs = Date.now() - date.getTime()
      const sec = Math.round(diffMs / 1000)
      if (sec < 45) return 'just now'
      if (sec < 90) return 'a minute ago'
      const min = Math.round(sec / 60)
      if (min < 45) return `${min} minutes ago`
      if (min < 90) return 'an hour ago'
      const hr = Math.round(min / 60)
      if (hr < 24) return `${hr} hours ago`
      if (hr < 36) return 'yesterday'
      const day = Math.round(hr / 24)
      if (day < 30) return `${day} days ago`
      if (day < 60) return 'last month'
      const mo = Math.round(day / 30)
      if (mo < 12) return `${mo} months ago`
      const yr = Math.round(day / 365)
      return yr === 1 ? 'a year ago' : `${yr} years ago`
    },

    // The /v1/tokens API returns Firestore Timestamps in their
    // JSON-serialized REST form: `{ _seconds, _nanoseconds }` (note
    // the underscores — the previous formatDate checked `.seconds`
    // without them, which is why every row rendered "Invalid Date").
    // Handle every shape we might see across the wire: the REST
    // shape, the client SDK Timestamp object (with `.toDate()`),
    // raw `{ seconds, nanoseconds }`, ISO strings, and numeric
    // millisecond timestamps.
    toDate (timestamp) {
      if (!timestamp) return null
      if (timestamp instanceof Date) return timestamp
      if (typeof timestamp === 'number') return new Date(timestamp)
      if (typeof timestamp === 'string') {
        const d = new Date(timestamp)
        return isNaN(d.getTime()) ? null : d
      }
      if (typeof timestamp === 'object') {
        if (typeof timestamp.toDate === 'function') {
          try { return timestamp.toDate() } catch (e) { /* fall through */ }
        }
        if (typeof timestamp._seconds === 'number') {
          return new Date(timestamp._seconds * 1000)
        }
        if (typeof timestamp.seconds === 'number') {
          return new Date(timestamp.seconds * 1000)
        }
      }
      return null
    }
  }
}
</script>

<style scoped>
/* Cap the farm picker so long lists stay scrollable inside the
   drawer instead of pushing the rest of the form below the fold. */
.farm-picker {
  max-height: 220px;
  overflow-y: auto;
  border: 1px solid #d6dbe0;
  border-radius: 4px;
  padding: 8px 10px;
  background: #fafbfc;
}
.farm-picker-row {
  margin-bottom: 2px;
}
</style>
